import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';

const Products = () => {
  const { products, loading, error } = useProducts();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'Tous les produits' },
    { value: 'fontaine', label: 'Fontaines' },
    { value: 'residentiel', label: 'Résidentiels' },
    { value: 'professionnel', label: 'Professionnels' },
    { value: 'industriel', label: 'Industriels' }
  ];

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(product => product.category === selectedCategory);

  const getCategoryDescription = (category) => {
    const descriptions = {
      fontaine: "Parfaites pour particuliers et bureaux",
      residentiel: "Solutions idéales pour foyers et petites entreprises",
      professionnel: "Pour hôtels, usines et exploitations",
      industriel: "Solutions sur mesure pour grandes industries et collectivités"
    };
    return descriptions[category] || "";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des produits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center bg-no-repeat text-white py-20"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_ecoweb-revamp/artifacts/slhqu5da_IMG_2913.png')"
        }}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 drop-shadow-lg">
            Nos Générateurs d'Eau Atmosphérique
          </h1>
          <p className="text-xl lg:text-2xl mb-8 text-blue-100 drop-shadow-md">
            De la fontaine domestique aux solutions industrielles
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.value}
                data-testid={`filter-${category.value}`}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  selectedCategory === category.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <>
            {selectedCategory !== 'all' && (
              <div className="text-center mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {categories.find(cat => cat.value === selectedCategory)?.label}
                </h2>
                <p className="text-gray-600">
                  {getCategoryDescription(selectedCategory)}
                </p>
              </div>
            )}

            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProducts.map((product) => (
                  <div key={product.id} data-testid={`product-card-${product.id}`} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow relative flex flex-col h-full">
                    <div className="relative h-64 bg-gray-50 p-4 flex items-center justify-center">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/300x200/2563eb/ffffff?text=${encodeURIComponent(product.name.split(' ')[2] || 'ECOCLIMA')}`;
                        }}
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h3>
                      <div className="mb-4">
                        <p className="text-lg font-bold text-blue-600 mb-1">
                          {product.capacity_liters} L/jour
                        </p>
                        {product.available_for_purchase ? (
                          <p className="text-xl font-bold text-green-600">
                            {product.price.toLocaleString('fr-FR')}€
                          </p>
                        ) : (
                          <p className="text-xl font-semibold text-green-600">
                            Prix sur devis
                          </p>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-4 flex-grow">
                        {product.description}
                      </p>
                      <div className="flex gap-2 mt-auto">
                        <Link
                          to={`/produits/${product.id}`}
                          data-testid={`product-details-${product.id}`}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg text-center font-medium transition-colors text-sm flex-1"
                        >
                          Voir les détails
                        </Link>
                        {product.available_for_purchase ? (
                          <Link
                            to={`/checkout?product=${product.id}`}
                            data-testid={`product-buy-${product.id}`}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg text-center font-medium transition-colors text-sm"
                          >
                            Commander
                          </Link>
                        ) : (
                          <Link
                            to={`/devis?product=${product.id}`}
                            data-testid={`product-quote-${product.id}`}
                            className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-4 py-3 rounded-lg text-center font-medium transition-colors text-sm"
                          >
                            Devis gratuit
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun produit trouvé</h3>
                <p className="text-gray-600">Essayez de sélectionner une autre catégorie.</p>
              </div>
            )}
          </>
        </div>
      </section>

      {/* Options and Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Options et services</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "Générateurs neufs", description: "Toute notre gamme CLOUD disponible", icon: "🏭" },
              { title: "Filtres & pièces", description: "Maintenance et remplacement", icon: "🔧" },
              { title: "Autonomie totale", description: "Panneaux solaires + batteries", icon: "☀️" },
              { title: "Installation & SAV", description: "Service complet par nos équipes", icon: "👨‍🔧" }
            ].map((service) => (
              <div key={service.title} className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6">
            Besoin d'aide pour choisir ?
          </h2>
          <p className="text-xl mb-8">
            Nos experts vous conseillent gratuitement pour trouver la solution parfaite
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/devis"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Demander un devis
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
